import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { ExportFormType } from '@connect-services/client/components/export-modal/export-modal.model';
import {
    FieldInterventionForm,
    FieldInterventionFormControllerGetRequestParams,
    FieldInterventionFormsService,
    PeopleService,
    Person,
    PersonControllerGetFieldInterventionOperatorsRequestParams,
} from '@connect-services/client/sdk/api';
import { CustomDateAdapterService } from '@connect-services/client/services/custom-date-adapter.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { ADAPTER_DATE_FORMATS, TranslateService } from '@connect-services/client/services/translate.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { SelectComponent } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { OrderEnum } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CheckboxComponent, RessourcesModule, SelectComponent],
    providers: [
        { deps: [TranslateService], provide: DateAdapter, useClass: CustomDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: ADAPTER_DATE_FORMATS },
    ],
    selector: 'app-export-modal-advanced-options',
    templateUrl: './export-modal-advanced-options.component.html',
})
export class ExportModalAdvancedOptionsComponent implements OnInit {
    public readonly assignedFormList: WritableSignal<FieldInterventionForm[]> = signal([]);
    public readonly assignedFormOptions = computed(() =>
        this.assignedFormList().map((form) => {
            return { label: form.name, value: form };
        }),
    );
    public readonly formGroup = input.required<FormGroup<ExportFormType>>();
    public readonly intervenedOperatorList: WritableSignal<Person[]> = signal([]);
    public readonly intervenedOperatorOptions = computed(() =>
        this.intervenedOperatorList().map((operator) => {
            return { label: operator.fullName, value: operator };
        }),
    );
    public readonly isAdvancedOptionsOpen = input.required<boolean>();
    public readonly operatorList: WritableSignal<Person[]> = signal([]);
    public readonly operatorOptions = computed(() =>
        this.operatorList().map((operator) => {
            return { label: operator.fullName, value: operator };
        }),
    );

    protected readonly CheckboxState = CheckboxState;
    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);

    private readonly fetchAllService = inject(FetchAllService);
    private readonly fieldInterventionFormsService = inject(FieldInterventionFormsService);
    private readonly peopleService = inject(PeopleService);

    public clearDateRange(): void {
        this.formGroup().patchValue({ endDate: null, startDate: null });
    }

    public async ngOnInit(): Promise<void> {
        if (!this.fieldInterventionBloc.value()) {
            return;
        }
        this.assignedFormList.set(await this.getAllFormAssigned());
        this.operatorList.set(await this.getAllOperators());
        this.intervenedOperatorList.set(await this.getAllOperators(true));
    }

    public selectAssignedPeopleOption(people: Person[]): void {
        this.formGroup().controls.assignedPeoples.setValue(people);
    }

    public selectFormOption(form: FieldInterventionForm[]): void {
        this.formGroup().controls.forms.setValue(form);
    }

    public selectIntervenedPeopleOption(people: Person[]): void {
        this.formGroup().controls.intervenedPeoples.setValue(people);
    }

    public toggleUpdatedEntities(): void {
        this.formGroup().controls.includeUpdatedEntities.setValue(
            !this.formGroup().controls.includeUpdatedEntities.value,
        );
    }

    private getAllFormAssigned(): Promise<FieldInterventionForm[]> {
        return this.fetchAllService.getAll<FieldInterventionFormControllerGetRequestParams, FieldInterventionForm>(
            this.fieldInterventionFormsService.fieldInterventionFormControllerGet.bind(
                this.fieldInterventionFormsService,
            ),
            {
                fieldIntervention: this.fieldInterventionBloc.value()!.id,
                order: OrderEnum.ASC,
                orderBy: 'name',
            },
        );
    }

    private getAllOperators(hasIntervened?: boolean): Promise<Person[]> {
        return this.fetchAllService.getAll<PersonControllerGetFieldInterventionOperatorsRequestParams, Person>(
            this.peopleService.personControllerGetFieldInterventionOperators.bind(this.peopleService),
            {
                fieldInterventionIds: [this.fieldInterventionBloc.value()!.id],
                hasIntervened: hasIntervened,
                order: 'ASC',
                orderBy: 'familyName',
            },
        );
    }
}
